/* eslint-disable react/no-unused-prop-types */
import {
  Box,
  HStack,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  GridItem,
  Grid,
  IconButton,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  TableContainer,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { date as commonDate, debugLog } from '@finley-technologies/common';

import { FundingRequestStatusEnumGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { FundingImpact } from './CreateRequest/FundingImpact';
import { DownloadAllPreviewFundingNoticesButton } from 'packages/funding-requests/ui/submit/DownloadAllPreviewFundingNoticesButton';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { InfoCard } from '~/packages/common-frontend/components/InfoCard';
import {
  dollarNumberWithCommas,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { DownloadAllLiveFundingNoticesFromFundingRequestButton } from '~/packages/funding-requests/ui/submit/DownloadAllLiveFundingNoticesFromFundingRequestButton';
import { DownloadLiveFundingNoticeFromFundingRequestButton } from '~/packages/funding-requests/ui/submit/DownloadLiveFundingNoticeFromFundingRequestButton';
import { DownloadPreviewFundingNoticeButton } from '~/packages/funding-requests/ui/submit/DownloadPreviewFundingNoticeButton';

type ReviewRequestProps = {
  canSeeInvoice?: boolean;
  status?: FundingRequestStatusEnumGQLType;
  fundingRequestId?: string;
};

const TransactionDetailsRow = ({
  label,
  panel,
  amount,
  isInitiallyOpen = false,
}: {
  panel: React.ReactNode;
  label: React.ReactNode;
  amount: React.ReactNode;
  isInitiallyOpen?: boolean;
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(isInitiallyOpen);

  useEffect(() => {
    setIsPanelOpen((previousIsPanelOpen) => {
      if (!previousIsPanelOpen) {
        return isInitiallyOpen;
      }
      return previousIsPanelOpen;
    });
  }, [isInitiallyOpen]);

  return (
    <>
      <GridItem
        bgColor={colors.aurora[50]}
        p="16px"
        colSpan={2}
        alignItems="center"
      >
        <Text fontWeight="bold">{label}</Text>
      </GridItem>
      <GridItem
        bgColor={colors.aurora[50]}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        p="0 16px"
      >
        <Text fontWeight="bold">{amount}</Text>
        <IconButton
          ml="16px"
          aria-label="expand transaction details"
          icon={
            <Icon
              icon={isPanelOpen ? 'circle-chevron-up' : 'circle-chevron-down'}
            />
          }
          variant="ghost"
          _focus={{ backgroundColor: 'none' }}
          _hover={{ backgroundColor: 'none' }}
          _active={{ backgroundColor: 'none' }}
          onClick={() => setIsPanelOpen(!isPanelOpen)}
        />
      </GridItem>
      {isPanelOpen && <GridItem gridColumn="1/-1">{panel}</GridItem>}
    </>
  );
};

const ClassWrapper = ({
  isClassWrapperDisabled,
  children,
}: {
  isClassWrapperDisabled?: boolean;
  children: React.ReactNode;
}) => {
  return isClassWrapperDisabled ? (
    <>{children}</>
  ) : (
    <Box
      w="100%"
      border={`1px solid ${colors.gray[200]}`}
      borderRadius="8px"
      p="24px"
    >
      {children}
    </Box>
  );
};

export const ReviewRequest = ({
  canSeeInvoice = false,
  status,
  fundingRequestId,
}: ReviewRequestProps) => {
  const { user } = useUserAuthOrThrow();
  // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base
  const isValleySls = user.organization.slug === 'valleyBankSls';

  // @jendevelops Placeholder until preview loans put back in
  debugLog({ canSeeInvoice });
  const {
    context: {
      allowedActions,
      reportingMechanics: { fundingNotices },
      data: {
        expectedSubmission: { draw, recycle },
      },
    },
    creditFacility: { id: creditFacilityId },
  } = useFundingRequestTemplateContextV1();

  const recycleRequestCount = recycle.requests.length;

  const drawRequestTargetFundingDate = draw.global.targetFundingDate;

  const recycleTargetFundingDate = recycle.global.targetFundingDate;

  // show download all funding notices menu if:
  // - not approved and target funding date is set (for preview notices) OR
  // - approved and funding request id is available (for live notices)
  const showDownloadAllFundingNoticesMenu =
    (status !== 'approved' && !!drawRequestTargetFundingDate) ||
    (status === 'approved' && !!fundingRequestId);

  return (
    <Tabs w="100%">
      <TabList>
        <Tab marginTop={0}>Request Submission</Tab>
        {allowedActions === 'mutable' && (
          <Tab marginTop={0}>Funding Impact</Tab>
        )}
      </TabList>
      <TabPanels>
        <TabPanel>
          <VStack spacing="40px" w="100%" alignItems="flex-start">
            <HStack spacing="32px" mt="40px" w="100%">
              {drawRequestTargetFundingDate && (
                <InfoCard
                  title="Funding Date"
                  value={commonDate.fullDateStrToHumanDateString(
                    drawRequestTargetFundingDate,
                  )}
                  icon={<Icon icon="calendar-plus" size="24px" />}
                  w="20rem"
                />
              )}
              {recycleRequestCount && recycleTargetFundingDate && (
                <InfoCard
                  title="Recycle Date"
                  value={commonDate.fullDateStrToHumanDateString(
                    recycleTargetFundingDate,
                  )}
                  icon={<Icon icon="calendar-plus" size="24px" />}
                  w="20rem"
                />
              )}
              {/* @jendevelops TODO: add in once loans are back in */}
              {/* <InfoCard
                title="Maturity Date"
                value={commonDate.fullDateStrToHumanDateString(maturityDate)}
                icon={<Icon icon="calendar-check" size="24px" />}
                w="100%"
              />
              <InfoCard
                title="Next Payment Date"
                value={commonDate.fullDateStrToHumanDateString(nextPaymentDate)}
                icon={<Icon icon="calendars" size="24px" />}
                w="100%"
              /> */}
            </HStack>
            {/* @jendevelops TODO: figure out how borrowers will be defined in the draw */}
            {/* <Box w="100%">
              <Text mb="16px" fontWeight="600">
                Preview Loan
              </Text>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Borrower</Th>
                    <Th>Draw Request</Th>
                    {hasRecycleRequest && <Th>Recycle Request</Th>}
                    <Th>Projected Interest</Th>
                    {canSeeInvoice && <Th>Invoice</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {draw.requests.map(
                    ({
                      borrower,
                      drawRequest,
                      recycleRequest,
                      projectedInterest,
                    }) => (
                      <Tr key={borrower}>
                        <Td>{borrower}</Td>
                        <Td>{dollarNumberWithCommas(drawRequest.amount)}</Td>
                        {hasRecycleRequest && (
                          <Td>
                            {dollarNumberWithCommas(recycleRequest.amount)}
                          </Td>
                        )}
                        <Td>{dollarNumberWithCommas(projectedInterest)}</Td>
                        {canSeeInvoice && (
                          <Td>
                            Invoice placeholder
                          </Td>
                        )}
                      </Tr>
                    ),
                  )}
                </Tbody>
              </Table>
            </Box> */}
            <Box w="100%">
              <Grid
                templateColumns="repeat(3, 1fr)"
                templateRows="repeat(1, 56px)"
                rowGap="16px"
              >
                {!!recycleRequestCount && (
                  <TransactionDetailsRow
                    label={
                      recycleRequestCount > 1
                        ? 'Recycle Requests'
                        : 'Recycle Request'
                    }
                    isInitiallyOpen={recycle.global.requestAmount > 0}
                    amount={dollarNumberWithCommas(
                      recycle.global.requestAmount,
                    )}
                    panel={
                      <TableContainer overflow="scroll" width="100%">
                        <Table variant="simple" mt="24px">
                          <Thead
                            background={colors.tableHeader}
                            borderBottom="1px solid lightgrey"
                            padding="8px 0"
                          >
                            <Tr>
                              <Th textAlign="left">Source</Th>
                              <Th textAlign="left">Destination</Th>
                              <Th textAlign="right">Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {recycle.requests.map((req, index) => (
                              <Tr key={req.key ?? index}>
                                <Td>{req.sourceBankAccount}</Td>
                                <Td>{req.destinationBankAccount}</Td>
                                <Td textAlign="right">
                                  {dollarNumberWithCommas(req.amount)}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    }
                  />
                )}

                <TransactionDetailsRow
                  isInitiallyOpen={draw.global.requestAmount > 0}
                  label="Draw Request"
                  amount={dollarNumberWithCommas(draw.global.requestAmount)}
                  panel={
                    <VStack w="100%" alignItems="flex-start" spacing="24px">
                      {draw.requests.map(
                        ({
                          key,
                          name: className,
                          amount,
                          // targetFundingDate,
                          shares,
                          pricingOptionKey,
                        }) => {
                          const isMultiClassed = draw.requests.length > 1;
                          const totalDrawRequestProjectedInterest = shares.reduce(
                            (acc, share) => acc + share.projectedInterest,
                            0,
                          );

                          return (
                            <ClassWrapper
                              isClassWrapperDisabled={!isMultiClassed}
                              key={key}
                            >
                              {isMultiClassed && (
                                <HStack w="100%" justify="space-between">
                                  <Text fontWeight="600">{className}</Text>
                                  <Text fontWeight="600">
                                    {dollarNumberWithCommas(amount)}
                                  </Text>
                                </HStack>
                              )}
                              <TableContainer overflow="scroll" width="100%">
                                <Table variant="simple" mt="24px">
                                  <Thead
                                    background={colors.tableHeader}
                                    borderBottom="1px solid lightgrey"
                                    padding="8px 0"
                                  >
                                    <Tr>
                                      <Th textAlign="left">Capital Provider</Th>
                                      <Th textAlign="left">Source</Th>
                                      <Th textAlign="left">Destination</Th>
                                      {isValleySls && (
                                        <Th textAlign="right">Pro Rata %</Th>
                                      )}
                                      <Th textAlign="right">
                                        {isValleySls ? 'Pro Rata $' : 'Amount'}
                                      </Th>
                                      {isValleySls && (
                                        <Th textAlign="right">
                                          Projected Interest
                                        </Th>
                                      )}
                                      {/* @jendevelops TODO Update funding notice to depend on if configured in CA from `fundingNotices` */}
                                      {isValleySls && (
                                        <Th textAlign="center">
                                          <Flex
                                            alignItems="center"
                                            justifyContent="center"
                                            gap="8px"
                                          >
                                            <Text>Notice</Text>
                                            {showDownloadAllFundingNoticesMenu && (
                                              <Menu>
                                                <MenuButton
                                                  fontSize="14px"
                                                  fontWeight="500"
                                                  aria-label="Download all funding notices"
                                                >
                                                  <HStack>
                                                    <Icon icon="chevron-down" />
                                                  </HStack>
                                                </MenuButton>
                                                <MenuList>
                                                  <MenuItem as="div">
                                                    {status === 'approved' &&
                                                      !!fundingRequestId && (
                                                        <DownloadAllLiveFundingNoticesFromFundingRequestButton
                                                          fundingRequestId={
                                                            fundingRequestId
                                                          }
                                                          commitmentKey={key}
                                                        />
                                                      )}
                                                    {status !== 'approved' &&
                                                      !!drawRequestTargetFundingDate && (
                                                        <DownloadAllPreviewFundingNoticesButton
                                                          creditFacilityId={
                                                            creditFacilityId
                                                          }
                                                          pricingOptionKey={
                                                            pricingOptionKey
                                                          }
                                                          totalDrawRequestProjectedInterest={
                                                            totalDrawRequestProjectedInterest
                                                          }
                                                          lenderShareSubmissions={
                                                            shares
                                                          }
                                                          targetFundingDate={
                                                            drawRequestTargetFundingDate
                                                          }
                                                        />
                                                      )}
                                                  </MenuItem>
                                                </MenuList>
                                              </Menu>
                                            )}
                                          </Flex>
                                        </Th>
                                      )}
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {shares.map((share) => {
                                      const {
                                        lender: {
                                          organizationId,
                                          name: lenderName,
                                        },
                                        bankAccounts: {
                                          source: sourceBankAccount,
                                          destination: destinationBankAccount,
                                        },
                                        percent: proRataPercentage,
                                        amount: lenderShareAmount,
                                        projectedInterest,
                                      } = share;

                                      // @jendevelops need to think through if multiple funding notices are ever needed for a single capital provider for the same type of FR component
                                      const fundingNotice = fundingNotices.find(
                                        (notice) =>
                                          notice.fundingRequestComponents.includes(
                                            'drawRequest',
                                          ),
                                      );

                                      const reportName =
                                        fundingNotice?.reportName ||
                                        'Funding Notice';

                                      return (
                                        <Tr key={organizationId}>
                                          <Td textAlign="left">{lenderName}</Td>
                                          <Td textAlign="left">
                                            {sourceBankAccount}
                                          </Td>
                                          <Td textAlign="left">
                                            {destinationBankAccount}
                                          </Td>
                                          {/*  ENG-12731 @jendevelops non valley customers might want a different proRata percentage displayed that also involves the other classes. Hide for non valley sls for now */}
                                          {isValleySls && (
                                            <Td textAlign="right">
                                              {percentageToPercentageLabel(
                                                proRataPercentage * 100,
                                                6,
                                              )}
                                            </Td>
                                          )}
                                          <Td textAlign="right">
                                            {dollarNumberWithCommas(
                                              lenderShareAmount,
                                            )}
                                          </Td>
                                          {/*  ENG-12731 @jendevelops TODO Update to hide column if projected interest is undefined from `FundingRequestGenerator` */}
                                          {isValleySls && (
                                            <Td textAlign="right">
                                              {dollarNumberWithCommas(
                                                projectedInterest,
                                              )}
                                            </Td>
                                          )}
                                          {/* ENG-12731 @jendevelops TODO Update funding notice to depend on if configured in CA from `fundingNotices` */}
                                          {isValleySls && (
                                            <Td textAlign="center">
                                              {/* @jendevelops need to pipe in target
                                            funding date for the lender share */}
                                              {status !== 'approved' &&
                                                !!drawRequestTargetFundingDate && (
                                                  <DownloadPreviewFundingNoticeButton
                                                    reportName={reportName}
                                                    targetFundingDate={
                                                      drawRequestTargetFundingDate
                                                    }
                                                    lenderShareSubmission={
                                                      share
                                                    }
                                                    pricingOptionKey={
                                                      pricingOptionKey
                                                    }
                                                    totalDrawRequestProjectedInterest={
                                                      totalDrawRequestProjectedInterest
                                                    }
                                                  />
                                                )}
                                              {status === 'approved' &&
                                                !!fundingRequestId && (
                                                  <DownloadLiveFundingNoticeFromFundingRequestButton
                                                    fundingRequestId={
                                                      fundingRequestId
                                                    }
                                                    commitmentKey={key}
                                                    capitalProviderOrganizationId={
                                                      organizationId
                                                    }
                                                  />
                                                )}
                                            </Td>
                                          )}
                                        </Tr>
                                      );
                                    })}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            </ClassWrapper>
                          );
                        },
                      )}
                    </VStack>
                  }
                />
              </Grid>
            </Box>
          </VStack>
        </TabPanel>
        {allowedActions === 'mutable' && (
          <TabPanel>
            <FundingImpact />
          </TabPanel>
        )}
      </TabPanels>
    </Tabs>
  );
};

const RequestSubmissionPanelProd = () => {
  const {
    context: {
      data: { expectedSubmission },
    },
  } = useFundingRequestTemplateContextV1();

  const hasRecycleRequest =
    expectedSubmission.recycle.requests.length > 0 &&
    expectedSubmission.recycle.global.requestAmount > 0;

  const isMultiClassed = expectedSubmission.draw.requests.length > 1;

  const drawTargetFundingDate =
    expectedSubmission.draw.global.targetFundingDate;

  const recycleTargetFundingDate =
    expectedSubmission.recycle.global.targetFundingDate;

  const hasMultipleRecycles =
    hasRecycleRequest && expectedSubmission.recycle.requests.length > 1;

  return (
    <VStack spacing="40px" w="100%" alignItems="flex-start">
      <HStack spacing="32px" mt="40px" w="100%">
        {/* @jendevelops TODO need to consider how we want to display for different recycle + funding dates */}
        {drawTargetFundingDate &&
          expectedSubmission.draw.global.requestAmount > 0 && (
            <InfoCard
              title="Target Funding Date"
              value={commonDate.fullDateStrToHumanDateString(
                drawTargetFundingDate,
              )}
              icon={<Icon icon="calendar-plus" size="24px" />}
              w="20em"
            />
          )}
        {recycleTargetFundingDate &&
          expectedSubmission.recycle.global.requestAmount > 0 && (
            <InfoCard
              title="Target Recycle Date"
              value={commonDate.fullDateStrToHumanDateString(
                recycleTargetFundingDate,
              )}
              icon={<Icon icon="calendar-plus" size="24px" />}
              w="20em"
            />
          )}
      </HStack>

      <Box w="100%">
        <Grid
          templateColumns="repeat(3, 1fr)"
          templateRows="repeat(1, 56px)"
          rowGap="16px"
        >
          {/* @makarand-cash: fix this to show both transfers */}
          {hasRecycleRequest && (
            <TransactionDetailsRow
              label={
                hasMultipleRecycles ? 'Recycle Requests' : 'Recycle Request'
              }
              isInitiallyOpen={
                expectedSubmission.recycle.global.requestAmount > 0
              }
              amount={dollarNumberWithCommas(
                expectedSubmission.recycle.global.requestAmount,
              )}
              panel={
                <Table variant="simple" mt="24px">
                  <Thead
                    background={colors.tableHeader}
                    borderBottom="1px solid lightgrey"
                    padding="8px 0"
                  >
                    <Tr>
                      <Th>Source</Th>
                      <Th> Destination</Th>
                      <Th textAlign="right">Amount</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {expectedSubmission.recycle.requests.map((req, index) => (
                      <Tr key={req.key ?? index}>
                        <Td>{req.sourceBankAccount}</Td>
                        <Td>{req.destinationBankAccount}</Td>
                        <Td textAlign="right">
                          {dollarNumberWithCommas(req.amount)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              }
            />
          )}
          {expectedSubmission.draw.requests.length > 0 && (
            <TransactionDetailsRow
              isInitiallyOpen={expectedSubmission.draw.global.requestAmount > 0}
              label="Draw Request"
              amount={dollarNumberWithCommas(
                expectedSubmission.draw.global.requestAmount,
              )}
              panel={
                <VStack w="100%" alignItems="flex-start" spacing="24px">
                  {expectedSubmission.draw.requests.map(
                    ({ key, name, amount, shares }) => {
                      return (
                        <ClassWrapper
                          isClassWrapperDisabled={!isMultiClassed}
                          key={key}
                        >
                          {isMultiClassed && (
                            <HStack w="100%" justify="space-between">
                              <Text fontWeight="600">{name}</Text>
                              <Text fontWeight="600">
                                {dollarNumberWithCommas(amount)}
                              </Text>
                            </HStack>
                          )}
                          <Table variant="simple" mt="24px">
                            <Thead
                              background={colors.tableHeader}
                              borderBottom="1px solid lightgrey"
                              padding="8px 0"
                            >
                              <Tr>
                                <Th textAlign="left">Capital Provider</Th>
                                <Th textAlign="left">Source</Th>
                                <Th textAlign="left">Destination</Th>
                                <Th textAlign="right">Pro Rata %</Th>
                                <Th textAlign="right">Pro Rata $</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {shares.map(
                                ({
                                  lender: { organizationId, name: lenderName },
                                  bankAccounts: {
                                    source: sourceBankAccount,
                                    destination: destinationBankAccount,
                                  },
                                  percent: proRataPercentage,
                                  amount: proRataAmount,
                                }) => (
                                  <Tr key={organizationId}>
                                    <Td textAlign="left">{lenderName}</Td>
                                    <Td textAlign="left">
                                      {sourceBankAccount}
                                    </Td>
                                    <Td textAlign="left">
                                      {destinationBankAccount}
                                    </Td>
                                    <Td textAlign="right">
                                      {percentageToPercentageLabel(
                                        proRataPercentage * 100,
                                        6,
                                      )}
                                    </Td>
                                    <Td textAlign="right">
                                      {dollarNumberWithCommas(proRataAmount)}
                                    </Td>
                                  </Tr>
                                ),
                              )}
                            </Tbody>
                          </Table>
                        </ClassWrapper>
                      );
                    },
                  )}
                </VStack>
              }
            />
          )}
        </Grid>
      </Box>
    </VStack>
  );
};

// @jendevelops This is temporary to get Tamara live
// Will eventually consolidate with ReviewRequest
export const ReviewRequestMultiClass = () => {
  const {
    context: { allowedActions },
  } = useFundingRequestTemplateContextV1();

  // omits funding impact until we can fix it for tamara
  return allowedActions === 'mutable' ? (
    <Tabs w="100%">
      <TabList>
        <Tab marginTop={0}>Request Submission</Tab>
        <Tab marginTop={0}>Funding Impact</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          <RequestSubmissionPanelProd />
        </TabPanel>
        <TabPanel>
          <FundingImpact />
        </TabPanel>
      </TabPanels>
    </Tabs>
  ) : (
    <RequestSubmissionPanelProd />
  );
};
